package org.example.project.enginechat

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.LoginService
import org.example.project.utils.All

object ChatApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadRooms(): List<All> =
        decodeList(LumaticaApiManager.call("chatmodulescreen/rooms"))

    suspend fun loadRoomsForParticipant(participantId: String): List<All> =
        if (participantId.isBlank()) emptyList()
        else decodeList(LumaticaApiManager.call("chatmodulescreen/rooms-for/${apiPart(participantId)}"))

    suspend fun loadRoomHeadsForParticipant(participantId: String): List<All> =
        if (participantId.isBlank()) emptyList()
        else decodeList(LumaticaApiManager.call("chatmodulescreen/room-heads-for/${apiPart(participantId)}"))

    suspend fun loadRoom(chatId: String): All? =
        if (chatId.isBlank()) null
        else decodeOne(LumaticaApiManager.call("chatmodulescreen/room/${apiPart(chatId)}"))

    suspend fun loadMessages(chatId: String): List<All> =
        if (chatId.isBlank()) emptyList()
        else decodeList(LumaticaApiManager.call("chatmodulescreen/messages/${apiPart(chatId)}"))

    suspend fun deleteRoomForParticipant(chatId: String, participantId: String): All? =
        if (chatId.isBlank() || participantId.isBlank()) null
        else decodeOne(LumaticaApiManager.call("chatmodulescreen/delete-for/${apiPart(chatId)}/${apiPart(participantId)}"))

    suspend fun markRead(chatId: String, participantId: String): All? =
        if (chatId.isBlank() || participantId.isBlank()) null
        else decodeOne(LumaticaApiManager.call("chatmodulescreen/mark-read/${apiPart(chatId)}/${apiPart(participantId)}"))

    suspend fun unreadCount(participantId: String): Int {
        if (participantId.isBlank()) return 0
        val raw = LumaticaApiManager.call("chatmodulescreen/unread-count/${apiPart(participantId)}")
        return Regex("\\\"count\\\"\\s*:\\s*(\\d+)").find(raw)?.groupValues?.getOrNull(1)?.toIntOrNull() ?: 0
    }

    suspend fun ensureRoom(
        participantIds: List<String>,
        roomName: String = "",
        firmenId: String = "",
        firma: String = "",
        roomId: String = "",
        scope: String = "",
        crId: String = ""
    ): All? {
        val participants = participantIds.map { it.trim() }.filter { it.isNotBlank() }.distinct().sorted()
        if (participants.isEmpty()) return null
        return decodeOne(
            LumaticaApiManager.call(
                "chatmodulescreen/ensure-room/${apiPart(participants.joinToString(","))}/${apiPart(roomName)}/${apiPart(firmenId)}/${apiPart(firma)}/${apiPart(roomId)}/${apiPart(scope)}/${apiPart(crId)}"
            )
        )
    }

    suspend fun sendMessage(
        chatId: String,
        participantIds: List<String>,
        senderId: String,
        text: String,
        firmenId: String = "",
        firma: String = ""
    ): All? {
        val participants = participantIds.map { it.trim() }.filter { it.isNotBlank() }.distinct().sorted()
        val userId = senderId.trim().ifBlank { LoginService.getId().trim() }
        if (chatId.isBlank() || userId.isBlank() || text.isBlank() || participants.isEmpty()) return null
        return decodeOne(
            LumaticaApiManager.call(
                "chatmodulescreen/send/${apiPart(chatId)}/${apiPart(participants.joinToString(","))}/${apiPart(userId)}/${apiPart(text)}/${apiPart(firmenId)}/${apiPart(firma)}"
            )
        )
    }

    private fun decodeOne(raw: String): All? =
        if (raw.isBlank() || raw == "null") null
        else runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
